import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { AuthProvider } from '@/contexts/AuthContext';
import { LicenseProvider } from '@/contexts/LicenseContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import WhatsAppButton from '@/components/WhatsAppButton';
import { preloadCriticalPages, LazyLoadingErrorBoundary } from '@/lib/performance';

// Lazy load pages for better performance (code splitting)
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const LicenseActivation = lazy(() => import('@/pages/LicenseActivation'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Tickets = lazy(() => import('@/pages/Tickets'));
const Connections = lazy(() => import('@/pages/Connections'));
const QuickReplies = lazy(() => import('@/pages/QuickReplies'));
const ScheduledMessages = lazy(() => import('@/pages/ScheduledMessages'));
const Campaigns = lazy(() => import('@/pages/Campaigns'));
const Tags = lazy(() => import('@/pages/Tags'));
const CRM = lazy(() => import('@/pages/CRM'));
const Quadros = lazy(() => import('@/pages/Quadros'));
const Settings = lazy(() => import('@/pages/Settings'));
const Admin = lazy(() => import('@/pages/Admin'));
const Queues = lazy(() => import('@/pages/Queues'));
const Contacts = lazy(() => import('@/pages/Contacts'));
const FlowBuilder = lazy(() => import('@/pages/FlowBuilder'));
const AIAgent = lazy(() => import('@/pages/AIAgent'));
const AIAgents = lazy(() => import('@/pages/AIAgents'));
const KnowledgeBase = lazy(() => import('@/pages/KnowledgeBase'));
const Users = lazy(() => import('@/pages/Users'));
const Integrations = lazy(() => import('@/pages/Integrations'));
const Companies = lazy(() => import('@/pages/Companies'));
const Plans = lazy(() => import('@/pages/Plans'));
const Billing = lazy(() => import('@/pages/Billing'));
const Coupons = lazy(() => import('@/pages/Coupons'));
const Finance = lazy(() => import('@/pages/Finance'));
const Proposals = lazy(() => import('@/pages/Proposals'));
const Reports = lazy(() => import('@/pages/Reports'));
const Calendar = lazy(() => import('@/pages/Calendar'));
const MediaLibrary = lazy(() => import('@/pages/MediaLibrary'));
const InternalChatPage = lazy(() => import('@/pages/InternalChatPage'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const GoogleCallback = lazy(() => import('@/pages/GoogleCallback'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-600 text-sm">Carregando...</p>
      </div>
    </div>
  );
}

// Component to conditionally show WhatsApp button on public pages
function ConditionalWhatsAppButton() {
  const location = useLocation();
  const publicPaths = ['/register', '/privacy', '/terms'];
  const shouldShowButton = publicPaths.includes(location.pathname);
  
  if (!shouldShowButton) return null;
  
  return (
    <WhatsAppButton 
      phoneNumber="556182511308"
      message="Olá! Gostaria de saber mais sobre o ChatVono."
      position="bottom-right"
      size="lg"
    />
  );
}

// Main App component wrapped with ErrorBoundary and performance optimizations
function AppContent() {
  const location = useLocation();
  
  // Preload critical pages after authentication
  useEffect(() => {
    const isProtectedPage = location.pathname.startsWith('/dashboard') || 
                           location.pathname.startsWith('/tickets') || 
                           location.pathname.startsWith('/contacts');
    
    if (isProtectedPage) {
      // Preload other critical pages in background
      preloadCriticalPages();
    }
  }, [location.pathname]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<Register />} />
        <Route path="/license-activation" element={<LicenseActivation />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/integrations/google-callback" element={<GoogleCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Billing route (no Layout, no payment check) */}
        <Route
          path="/billing"
          element={
            <ProtectedRoute skipBillingCheck>
              <Billing />
            </ProtectedRoute>
          }
        />

        {/* Admin route (no Layout) */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<ProtectedRoute requiredPermission="canViewDashboard"><Dashboard /></ProtectedRoute>} />
          <Route path="/tickets" element={<ProtectedRoute requiredPermission="canViewTickets"><Tickets /></ProtectedRoute>} />
          <Route path="/tickets/:ticketId" element={<ProtectedRoute requiredPermission="canViewTickets"><Tickets /></ProtectedRoute>} />
          <Route path="/connections" element={<ProtectedRoute requiredPermission="canViewConnections"><Connections /></ProtectedRoute>} />
          <Route path="/queues" element={<ProtectedRoute requiredPermission="canViewQueues"><Queues /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute requiredPermission="canViewContacts"><Contacts /></ProtectedRoute>} />
          <Route path="/quick-replies" element={<ProtectedRoute requiredPermission="canViewQuickReplies"><QuickReplies /></ProtectedRoute>} />
          <Route path="/scheduled-messages" element={<ProtectedRoute requiredPermission="canViewScheduledMessages"><ScheduledMessages /></ProtectedRoute>} />
          <Route path="/campaigns" element={<ProtectedRoute requiredPermission="canViewCampaigns"><Campaigns /></ProtectedRoute>} />
          <Route path="/tags" element={<ProtectedRoute requiredPermission="canViewTags"><Tags /></ProtectedRoute>} />
          <Route path="/crm" element={<ProtectedRoute requiredPermission="canViewCRM"><CRM /></ProtectedRoute>} />
          <Route path="/quadros" element={<ProtectedRoute><Quadros /></ProtectedRoute>} />
          <Route path="/quadros/:id" element={<ProtectedRoute><Quadros /></ProtectedRoute>} />
          <Route path="/flows" element={<ProtectedRoute requiredPermission="canViewFlows"><FlowBuilder /></ProtectedRoute>} />
          <Route path="/ai-agents" element={<ProtectedRoute requiredPermission="canViewAgents"><AIAgents /></ProtectedRoute>} />
          <Route path="/knowledge-base" element={<ProtectedRoute requiredPermission="canViewKnowledgeBase"><KnowledgeBase /></ProtectedRoute>} />
          <Route path="/ai-agent" element={<ProtectedRoute requiredPermission="canViewAgents"><AIAgent /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute requiredPermission="canViewUsers"><Users /></ProtectedRoute>} />
          <Route path="/integrations" element={<ProtectedRoute requiredPermission="canManageIntegrations"><Integrations /></ProtectedRoute>} />
          <Route path="/companies" element={<ProtectedRoute requiredRole="admin"><Companies /></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute requiredRole="admin"><Plans /></ProtectedRoute>} />
          <Route path="/coupons" element={<ProtectedRoute requiredRole="admin"><Coupons /></ProtectedRoute>} />
          <Route path="/financial" element={<ProtectedRoute requiredPermission="canViewFinance"><Finance /></ProtectedRoute>} />
          <Route path="/finance" element={<ProtectedRoute requiredPermission="canViewFinance"><Finance /></ProtectedRoute>} />
          <Route path="/proposals" element={<ProtectedRoute requiredPermission="canManageDeals"><Proposals /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute requiredPermission="canViewReports"><Reports /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute requiredPermission="canViewCalendar"><Calendar /></ProtectedRoute>} />
          <Route path="/media-library" element={<ProtectedRoute requiredPermission="canViewQuickReplies"><MediaLibrary /></ProtectedRoute>} />
          <Route path="/internal-chat" element={<ProtectedRoute requiredPermission="canViewInternalChat"><InternalChatPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute requiredPermission="canViewSettings"><Settings /></ProtectedRoute>} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <LazyLoadingErrorBoundary>
        <LicenseProvider>
          <AuthProvider>
            {/* Default SEO for the entire app */}
            <SEO />

            <AppContent />
            
            {/* Conditional WhatsApp Button for public pages */}
            <ConditionalWhatsAppButton />
          </AuthProvider>
        </LicenseProvider>
      </LazyLoadingErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
